import React from "react";
import { useBoolean } from "@fluentui/react-hooks";
import { Icon } from "@fluentui/react";
import CopilotPanel from "../pages/CopilotPanel";
import CopilotLogo from "../assets/Microsoft-Copilot-Logo.png";

const FloatingCopilotButton: React.FC = () => {
  const [isCopilotOpen, { setTrue: openCopilotPanel, setFalse: dismissCopilotPanel }] = useBoolean(false);
  const [showPrompts, setShowPrompts] = React.useState(false);
  const [selectedPrompt, setSelectedPrompt] = React.useState<string | null>(null);
  const [currentSessionId, setCurrentSessionId] = React.useState<string>("");

  const prompts = [
    "Summarize the data on this page.",
    "What all data sources are being used here?",
    "What's the latest information?",
  ];

  // Add effect to adjust main content when panel opens/closes
  React.useEffect(() => {
    const mainElement = document.getElementById("main");
    const scrollablePanes = document.querySelectorAll("[class*=\"scrollablePane\"]");

    if (isCopilotOpen) {
      // Use clamp to match the panel's actual width
      const panelWidth = "clamp(320px, 30vw, 600px)";

      // Adjust main element
      if (mainElement) {
        mainElement.style.marginRight = panelWidth;
        mainElement.style.transition = "margin-right 0.3s ease";
      }

      // Adjust scrollable panes
      scrollablePanes.forEach((pane) => {
        const element = pane as HTMLElement;
        element.style.right = panelWidth;
        element.style.transition = "right 0.3s ease";
      });

      // Adjust body to prevent overflow
      document.body.style.overflowX = "hidden";
    } else {
      // Reset main element
      if (mainElement) {
        mainElement.style.marginRight = "0";
      }

      // Reset scrollable panes
      scrollablePanes.forEach((pane) => {
        const element = pane as HTMLElement;
        element.style.right = "0";
      });

      // Reset body overflow
      document.body.style.overflowX = "";
    }

    return () => {
      if (mainElement) {
        mainElement.style.marginRight = "0";
      }
      scrollablePanes.forEach((pane) => {
        const element = pane as HTMLElement;
        element.style.right = "0";
      });
      document.body.style.overflowX = "";
    };
  }, [isCopilotOpen]);

  const headerLogoStyle: React.CSSProperties = {
    width: "40px",
    height: "40px",
    objectFit: "contain",
  };

  const customHeader = () => (
    <div style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      fontSize: "18px",
      fontWeight: "600",
      width: "100%",
      padding: "16px 20px",
      borderBottom: "1px solid #e1e4e8",
      backgroundColor: "#fff"
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <img src={CopilotLogo} alt="Copilot" style={headerLogoStyle} />
        <span>Security360 Copilot</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", paddingRight: "40px" }}>
        <button
          onClick={() => {
            if (currentSessionId) {
              const url = new URL("/aihub", window.location.origin);
              url.searchParams.set("sessionId", currentSessionId);
              window.location.href = url.toString();
            }
          }}
          disabled={!currentSessionId}
          style={{
            padding: "6px 12px",
            background: "transparent",
            border: currentSessionId ? "1px solid #0078d4" : "1px solid #c8c6c4",
            cursor: currentSessionId ? "pointer" : "not-allowed",
            fontSize: 12,
            color: currentSessionId ? "#0078d4" : "#c8c6c4",
            borderRadius: 4,
            display: "flex",
            alignItems: "center",
            gap: 6,
            opacity: currentSessionId ? 1 : 0.5,
            fontWeight: 600
          }}
          title={currentSessionId ? "Open in AI Hub" : "Start a chat to enable"}
        >
          <Icon iconName="OpenInNewWindow" style={{ fontSize: 12 }} />
          Open in AI Hub
        </button>
        <button
          onClick={dismissCopilotPanel}
          style={{
            padding: "6px 12px",
            background: "transparent",
            border: "1px solid #605e5c",
            color: "#605e5c",
            borderRadius: 4,
            cursor: "pointer",
            fontSize: 12,
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 6
          }}
          title="Close panel"
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = "#323130";
            e.currentTarget.style.color = "#323130";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = "#605e5c";
            e.currentTarget.style.color = "#605e5c";
          }}
        >
          <Icon iconName="Cancel" style={{ fontSize: 12 }} />
          Close
        </button>
      </div>
    </div>
  );

  const floatingButtonStyle: React.CSSProperties = {
    position: "fixed",
    bottom: "40px",
    right: "50px",
    width: "64px",
    height: "64px",
    borderRadius: "50%",
    backgroundColor: "#ffffff",
    color: "white",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    zIndex: 1000,
    cursor: "pointer",
    border: "none",
    transition: "all 0.3s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "4px",
  };

  const logoStyle: React.CSSProperties = {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  };

  const handlePromptClick = (prompt: string) => {
    setSelectedPrompt(prompt);
    setShowPrompts(false);
    openCopilotPanel();
  };

  return (
    <>
      {/* Prompts overlay */}
      {showPrompts && (
        <div
          onMouseEnter={() => setShowPrompts(true)}
          onMouseLeave={() => setShowPrompts(false)}
          style={{
            position: "fixed",
            bottom: "40px",
            right: "50px",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            zIndex: 999,
            paddingBottom: "80px",
          }}
        >
          {prompts.map((prompt, idx) => (
            <button
              key={idx}
              onClick={() => handlePromptClick(prompt)}
              style={{
                padding: "12px 16px",
                background: "#fff",
                border: "1px solid #e1e4e8",
                borderRadius: 16,
                cursor: "pointer",
                fontSize: 13,
                color: "#242424",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                transition: "all 0.2s ease",
                textAlign: "left",
                minWidth: "250px",
                maxWidth: "300px",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#f6f8fa";
                e.currentTarget.style.borderColor = "#0078d4";
                e.currentTarget.style.transform = "translateX(-5px)";
                e.currentTarget.style.boxShadow = "0 6px 16px rgba(0,120,212,0.2)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#fff";
                e.currentTarget.style.borderColor = "#e1e4e8";
                e.currentTarget.style.transform = "translateX(0)";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)";
              }}
            >
              {prompt}
            </button>
          ))}
        </div>
      )}

      <div
        title="AI Assistant"
        aria-label="Open AI Assistant"
        onClick={openCopilotPanel}
        style={floatingButtonStyle}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLElement).style.transform = "scale(1.1)";
          (e.currentTarget as HTMLElement).style.boxShadow = "0 6px 16px rgba(0, 0, 0, 0.2)";
          setShowPrompts(true);
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.transform = "scale(1)";
          (e.currentTarget as HTMLElement).style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.15)";
          setShowPrompts(false);
        }}
        role="button"
        tabIndex={0}
      >
        <img src={CopilotLogo} alt="Copilot" style={logoStyle} />
      </div>

      {/* Custom side panel */}
      {isCopilotOpen && (
        <div
          style={{
            position: "fixed",
            top: "48px",
            right: 0,
            bottom: 0,
            width: "clamp(320px, 30vw, 600px)",
            backgroundColor: "#fff",
            boxShadow: "-2px 0 8px rgba(0, 0, 0, 0.1)",
            zIndex: 1000,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden"
          }}
        >
          {customHeader()}
          <div style={{ flex: 1, overflow: "hidden" }}>
            <CopilotPanel
              initialPrompt={selectedPrompt}
              onPromptProcessed={() => setSelectedPrompt(null)}
              onClose={dismissCopilotPanel}
              isQuickAction={true}
              onSessionIdChange={setCurrentSessionId}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default FloatingCopilotButton;
